import { createContext, useCallback, useEffect, useState, ReactNode } from 'react';
import { authService, AuthUser } from '../services/auth.service';
import { INTERNAL_ROLES, InternalRole } from '../config/role-routes';

export interface AuthContextValue {
  user: AuthUser | null;
  accessToken: string | null;
  isLoading: boolean;
  isAuthenticated: boolean;
  login: (user: AuthUser, accessToken: string) => void;
  logout: () => Promise<void>;
  refreshUser: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | undefined>(undefined);

function isInternalRole(role: string): boolean {
  return INTERNAL_ROLES.includes(role as InternalRole);
}

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [accessToken, setAccessToken] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;

    async function bootstrap() {
      const token = authService.getToken();
      const cachedUser = authService.getUser();

      if (!token || !cachedUser) {
        if (!cancelled) {
          setIsLoading(false);
        }
        return;
      }

      if (!isInternalRole(cachedUser.role)) {
        authService.clearSession();
        if (!cancelled) {
          setUser(null);
          setAccessToken(null);
          setIsLoading(false);
        }
        return;
      }

      setUser(cachedUser);
      setAccessToken(token);

      const verified = await authService.verifyToken();
      if (cancelled) return;

      if (!verified || !isInternalRole(verified.role)) {
        authService.clearSession();
        setUser(null);
        setAccessToken(null);
      } else {
        setUser(verified);
      }

      setIsLoading(false);
    }

    bootstrap();
    return () => {
      cancelled = true;
    };
  }, []);

  const login = useCallback((newUser: AuthUser, newToken: string) => {
    setUser(newUser);
    setAccessToken(newToken);
  }, []);

  const logout = useCallback(async () => {
    await authService.logout();
    setUser(null);
    setAccessToken(null);
  }, []);

  const refreshUser = useCallback(async () => {
    const verified = await authService.verifyToken();
    if (verified && isInternalRole(verified.role)) {
      setUser(verified);
    }
  }, []);

  return (
    <AuthContext.Provider
      value={{
        user,
        accessToken,
        isLoading,
        isAuthenticated: !!user && !!accessToken,
        login,
        logout,
        refreshUser,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}